// Decoded-frame cache for cine playback.
//
// Holds CPU-visible RGB bytes packed for direct copy into VTK's 3-component
// scalar buffer. Bounded by a byte budget; LRU eviction. Per-image instance —
// disposed alongside the DicomCineImage that owns it.

import { decodeJpegInWorker, decodeJpegLsInWorker } from './jpegDecodePool';
import type { DecodedFrame } from './nativeFrame';
import type { JpegLsLayout } from './jpegLsDecode';

export { decodeNativeFrame } from './nativeFrame';
export type { DecodedFrame } from './nativeFrame';

// Returns false when the buffers disagree on pixel count, so callers can skip
// the publish step instead of rendering a partial frame.
export function copyDecodedFrameToRgb(
  frame: DecodedFrame,
  out: Uint8Array
): boolean {
  if (frame.rgb.length !== out.length) return false;
  out.set(frame.rgb);
  return true;
}

const DEFAULT_BUDGET_BYTES = 64 * 1024 * 1024;

export class FrameCache {
  private readonly budgetBytes: number;
  private bytesInUse: number;
  // Insertion-ordered Map gives O(1) LRU behavior.
  private readonly entries: Map<number, DecodedFrame>;

  constructor(budgetBytes: number = DEFAULT_BUDGET_BYTES) {
    this.budgetBytes = budgetBytes;
    this.bytesInUse = 0;
    this.entries = new Map();
  }

  get(frameIndex: number): DecodedFrame | null {
    const entry = this.entries.get(frameIndex);
    if (!entry) return null;
    // Move to "most recent" by re-inserting.
    this.entries.delete(frameIndex);
    this.entries.set(frameIndex, entry);
    return entry;
  }

  set(frameIndex: number, frame: DecodedFrame): void {
    const existing = this.entries.get(frameIndex);
    if (existing) {
      this.bytesInUse -= existing.rgb.byteLength;
      this.entries.delete(frameIndex);
    }
    this.entries.set(frameIndex, frame);
    this.bytesInUse += frame.rgb.byteLength;
    this.evictUntilUnderBudget();
  }

  has(frameIndex: number): boolean {
    return this.entries.has(frameIndex);
  }

  clear(): void {
    this.entries.clear();
    this.bytesInUse = 0;
  }

  size(): number {
    return this.entries.size;
  }

  getBytesInUse(): number {
    return this.bytesInUse;
  }

  private evictUntilUnderBudget(): void {
    if (this.bytesInUse <= this.budgetBytes) return;
    // Map iteration order is insertion order; the oldest entry is the first.
    for (const key of this.entries.keys()) {
      if (this.bytesInUse <= this.budgetBytes) break;
      const entry = this.entries.get(key)!;
      this.bytesInUse -= entry.rgb.byteLength;
      this.entries.delete(key);
    }
  }
}

export function decodeJpegFrame(
  bytes: Uint8Array,
  expectedWidth: number,
  expectedHeight: number
): Promise<DecodedFrame> {
  return decodeJpegInWorker(bytes, expectedWidth, expectedHeight);
}

export function decodeJpegLsFrame(
  bytes: Uint8Array,
  layout: JpegLsLayout
): Promise<DecodedFrame> {
  return decodeJpegLsInWorker(bytes, layout);
}
